/* Tiny event bus + hooks to refresh data after mutations.
   - dispatchOrioEvent("reminders-changed") after creating/completing/deleting reminders
   - useOrioRefresh(fn, deps) re-runs fn on: mount, custom event, tab focus, route change to specific path
*/
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

export const ORIO_EVENTS = {
  REMINDERS_CHANGED: "orio:reminders-changed",
  DOCUMENTS_CHANGED: "orio:documents-changed",
  DOG_CHANGED: "orio:dog-changed",
};

export function dispatchOrio(event) {
  try {
    window.dispatchEvent(new CustomEvent(event));
  } catch { /* SSR / older browsers */ }
}

/**
 * Re-runs `fn`:
 *   - once on mount
 *   - whenever the listed custom events fire
 *   - when the tab regains visibility
 *   - when the route pathname matches `refreshOnPath` (regex or string)
 */
export function useOrioRefresh(fn, { events = [], refreshOnPath = null } = {}) {
  const location = useLocation();
  useEffect(() => {
    fn();
    const handlers = events.map((ev) => {
      const h = () => fn();
      window.addEventListener(ev, h);
      return [ev, h];
    });
    const onVis = () => { if (document.visibilityState === "visible") fn(); };
    window.addEventListener("visibilitychange", onVis);
    return () => {
      handlers.forEach(([ev, h]) => window.removeEventListener(ev, h));
      window.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh on pathname match — important: re-runs when Home/Cassetto navigation occurs
  useEffect(() => {
    if (!refreshOnPath) return;
    const ok = typeof refreshOnPath === "string"
      ? location.pathname === refreshOnPath
      : refreshOnPath.test(location.pathname);
    if (ok) fn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
}
